/**
 * Maps ERP payment terms codes to human-readable descriptions.
 * Source: VendorTerms_FactTable.xlsx
 *
 * DISC_TERM_NODY = discount window days
 * NET_TERM_NODY  = net payment days (in fact table this column stores discount window;
 *                  actual net days are derived from TERM_DESC)
 */

export interface TermsDefinition {
  code: string;
  description: string;
  netDays: number;
  discountPct: number | null;
  discountDays: number | null;
}

const termsMap: Record<string, TermsDefinition> = {
  // Discount terms
  "015": {
    code: "015",
    description: "1.5% 10, Net 30",
    netDays: 30,
    discountPct: 1.5,
    discountDays: 10,
  },
  "1-45": {
    code: "1-45",
    description: "1% 10, Net 45",
    netDays: 45,
    discountPct: 1,
    discountDays: 10,
  },
  "105": {
    code: "105",
    description: "0.5% 10, Net 30",
    netDays: 30,
    discountPct: 0.5,
    discountDays: 10,
  },
  "110": {
    code: "110",
    description: "1% 10, Net 30",
    netDays: 30,
    discountPct: 1,
    discountDays: 10,
  },
  "115": {
    code: "115",
    description: "1% 15, Net 30",
    netDays: 30,
    discountPct: 1,
    discountDays: 15,
  },
  "130": {
    code: "130",
    description: "1% 30, Net 60",
    netDays: 60,
    discountPct: 1,
    discountDays: 30,
  },
  "1545": {
    code: "1545",
    description: "1.5% 10, Net 45",
    netDays: 45,
    discountPct: 1.5,
    discountDays: 10,
  },
  "2-30": {
    code: "2-30",
    description: "2% 30, Net 30",
    netDays: 30,
    discountPct: 2,
    discountDays: 30,
  },
  "2-45": {
    code: "2-45",
    description: "2% 15, Net 45",
    netDays: 45,
    discountPct: 2,
    discountDays: 15,
  },
  "210": {
    code: "210",
    description: "2% 10, Net 30",
    netDays: 30,
    discountPct: 2,
    discountDays: 10,
  },
  "215": {
    code: "215",
    description: "2% 15, Net 30",
    netDays: 30,
    discountPct: 2,
    discountDays: 15,
  },
  "220": {
    code: "220",
    description: "2% 20, Net 60",
    netDays: 60,
    discountPct: 2,
    discountDays: 20,
  },
  "230": {
    code: "230",
    description: "2% 30, Net 60",
    netDays: 60,
    discountPct: 2,
    discountDays: 30,
  },
  "245": {
    code: "245",
    description: "2% 10, Net 45",
    netDays: 45,
    discountPct: 2,
    discountDays: 10,
  },
  "260": {
    code: "260",
    description: "2% 10, Net 60",
    netDays: 60,
    discountPct: 2,
    discountDays: 10,
  },
  "310": {
    code: "310",
    description: "3% 10, Net 30",
    netDays: 30,
    discountPct: 3,
    discountDays: 10,
  },
  "410": {
    code: "410",
    description: "4% 10, Net 30",
    netDays: 30,
    discountPct: 4,
    discountDays: 10,
  },
  "510": {
    code: "510",
    description: "5% 10, Net 30",
    netDays: 30,
    discountPct: 5,
    discountDays: 10,
  },
  "580": {
    code: "580",
    description: "5% 10, Net 180",
    netDays: 180,
    discountPct: 5,
    discountDays: 10,
  },

  // Special terms
  "3010": {
    code: "3010",
    description: "30 days EOM +10",
    netDays: 40,
    discountPct: null,
    discountDays: null,
  },
  "53P": {
    code: "53P",
    description: "5th 3rd Prox",
    netDays: 70,
    discountPct: null,
    discountDays: null,
  },

  // Net terms (no discount)
  N7: { code: "N7", description: "Net 7 Days", netDays: 7, discountPct: null, discountDays: null },
  N10: {
    code: "N10",
    description: "Net 10 Days",
    netDays: 10,
    discountPct: null,
    discountDays: null,
  },
  N15: {
    code: "N15",
    description: "Net 15 Days",
    netDays: 15,
    discountPct: null,
    discountDays: null,
  },
  N20: {
    code: "N20",
    description: "Net 20 Days",
    netDays: 20,
    discountPct: null,
    discountDays: null,
  },
  N28: {
    code: "N28",
    description: "Net 28 Days",
    netDays: 28,
    discountPct: null,
    discountDays: null,
  },
  N30: {
    code: "N30",
    description: "Net 30 Days",
    netDays: 30,
    discountPct: null,
    discountDays: null,
  },
  N30D: {
    code: "N30D",
    description: "Net 30, DDU PP&A",
    netDays: 30,
    discountPct: null,
    discountDays: null,
  },
  N45: {
    code: "N45",
    description: "Net 45 Days",
    netDays: 45,
    discountPct: null,
    discountDays: null,
  },
  N60: {
    code: "N60",
    description: "Net 60 Days",
    netDays: 60,
    discountPct: null,
    discountDays: null,
  },
  N75: {
    code: "N75",
    description: "Net 75 Days",
    netDays: 75,
    discountPct: null,
    discountDays: null,
  },
  N90: {
    code: "N90",
    description: "Net 90 Days",
    netDays: 90,
    discountPct: null,
    discountDays: null,
  },
  N120: {
    code: "N120",
    description: "Net 120 Days",
    netDays: 120,
    discountPct: null,
    discountDays: null,
  },
  N180: {
    code: "N180",
    description: "Net 180 Days",
    netDays: 180,
    discountPct: null,
    discountDays: null,
  },
  NET: {
    code: "NET",
    description: "Net Due Upon Receipt",
    netDays: 1,
    discountPct: null,
    discountDays: null,
  },

  // Zero-day / special payment methods
  CC: { code: "CC", description: "Credit Card", netDays: 0, discountPct: null, discountDays: null },
  COD: {
    code: "COD",
    description: "Cash on Delivery",
    netDays: 0,
    discountPct: null,
    discountDays: null,
  },
  LC: {
    code: "LC",
    description: "Letter of Credit",
    netDays: 0,
    discountPct: null,
    discountDays: null,
  },
  PPD: { code: "PPD", description: "Prepaid", netDays: 0, discountPct: null, discountDays: null },
  SD: { code: "SD", description: "Sight Draft", netDays: 0, discountPct: null, discountDays: null },
  TT: {
    code: "TT",
    description: "Telegraph Transmittal",
    netDays: 0,
    discountPct: null,
    discountDays: null,
  },
  "-": {
    code: "-",
    description: "Terms identified elsewhere",
    netDays: 0,
    discountPct: null,
    discountDays: null,
  },
};

export function lookupTermsCode(code: string): TermsDefinition | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  return termsMap[upper] ?? termsMap[code.trim()] ?? null;
}

export function describeTermsCode(code: string): string {
  const def = lookupTermsCode(code);
  return def?.description ?? code;
}
